// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import dynamicProto from "@microsoft/dynamicproto-js";
import { getGlobal, getJSON, isNotNullOrUndefined, onConfigChange } from "@microsoft/applicationinsights-core-js";
import { createAsyncRejectedPromise } from "@nevware21/ts-async";
import { batchDropNotification, forEachMap, getEndpointDomain, getTimeFromId, getTimeId } from "../Helpers/Utils";
import { PayloadHelper } from "../PayloadHelper";
import { _DYN_ADD_EVENT, _DYN_CLEAR, _DYN_CRITICAL_CNT, _DYN_DIAG_LOG, _DYN_ENDPOINT, _DYN_EVTS, _DYN_GET_NEXT_BATCH, _DYN_GET_TIME, _DYN_INITIALIZE, _DYN_IN_STORAGE_MAX_TIME, _DYN_IS_ARR, _DYN_ITEM_CTX, _DYN_LAST_ACCESS_TIME, _DYN_LENGTH, _DYN_MAX_CRITICAL_EVTS_DR3, _DYN_NAME, _DYN_NOTIFICATION_MGR, _DYN_PUSH, _DYN_REMOVE_EVENTS, _DYN_REMOVE_ITEM, _DYN_STORAGE_CONFIG, _DYN_STORAGE_KEY_PREFIX, _DYN_SUPPORTS_SYNC_REQUES4, _DYN_TEARDOWN, _DYN__EVENTS_TO_DROP_PER_2 } from "../__DynamicConstants";
//TODO: move all const to one file
var EventsToDropAtOneTime = 10;
var Version = "1";
var DefaultStorageKey = "AIOffline";
var DefaultMaxStorageSizeInBytes = 5000000;
var MaxCriticalEvtsDropCnt = 2;
var DefaultMaxInStorageTime = 10080000; //7*24*60*60*1000 7days
// Private helper methods that are not exposed as class methods
function _isQuotaExceeded(storage, e) {
    var result = false;
    if (e instanceof DOMException) {
        // test name field too, because code might not be present
        if (e.code === 22 || e[_DYN_NAME /* @min:%2ename */] === "QuotaExceededError" || // everything except Firefox
            e.code === 1014 || e[_DYN_NAME /* @min:%2ename */] === "NS_ERROR_DOM_QUOTA_REACHED") { // Firefox
            if (storage && storage[_DYN_LENGTH /* @min:%2elength */] !== 0) {
                // acknowledge QuotaExceededError only if there's something already stored
                result = true;
            }
        }
    }
    return result;
}
/**
* Check and return that the storage type exists and has space to use
*/
function _getAvailableStorage(type) {
    var global = getGlobal() || {};
    var storage = null;
    try {
        storage = (global[type]);
        if (storage) {
            var x = "__storage_test__";
            storage.setItem(x, x);
            storage[_DYN_REMOVE_ITEM /* @min:%2eremoveItem */](x);
        }
    }
    catch (e) {
        if (!_isQuotaExceeded(storage, e)) {
            // If not Quota exception then assume not available
            storage = null;
        }
    }
    return storage;
}
// will drop batches with no critical evts first
function _dropEventsUpToPersistence(maxCnt, events, eventsToDropAtOneTime) {
    var dropKeys = [];
    var persistenceCnt = 0;
    var droppedEvents = 0;
    while (persistenceCnt <= maxCnt && droppedEvents < eventsToDropAtOneTime) {
        forEachMap(events, function (evt, key) {
            if (evt[_DYN_CRITICAL_CNT /* @min:%2ecriticalCnt */] === persistenceCnt) {
                dropKeys[_DYN_PUSH /* @min:%2epush */](key);
                droppedEvents++;
            }
            return (droppedEvents < eventsToDropAtOneTime);
        });
        if (droppedEvents > 0) {
            for (var lp = 0; lp < dropKeys[_DYN_LENGTH /* @min:%2elength */]; lp++) {
                delete events[dropKeys[lp]];
            }
            return droppedEvents;
        }
        persistenceCnt++;
    }
    return droppedEvents;
}
function _dropMaxTimeEvents(maxStorageTime, events, eventsToDropAtOneTime, mgr) {
    var dropKeys = [];
    var droppedEvents = 0;
    var currentTime = (new Date())[_DYN_GET_TIME /* @min:%2egetTime */]() + 1; // handle appended random float number
    var minStartTime = (currentTime - maxStorageTime);
    try {
        forEachMap(events, function (evt, key) {
            var id = getTimeFromId(key);
            if (id <= minStartTime) {
                dropKeys[_DYN_PUSH /* @min:%2epush */](key);
                droppedEvents++;
            }
            return (droppedEvents < eventsToDropAtOneTime);
        });
        if (droppedEvents > 0) {
            for (var lp = 0; lp < dropKeys[_DYN_LENGTH /* @min:%2elength */]; lp++) {
                delete events[dropKeys[lp]];
            }
            if (mgr) {
                batchDropNotification(mgr, droppedEvents, 3 /* eBatchDiscardedReason.MaxInStorageTimeExceeded */);
            }
            return true;
        }
    }
    catch (e) {
        // catch drop events error
    }
    return droppedEvents > 0;
}
/**
 * Class that implements storing of events using the WebStorage Api ((window||globalThis||self).localstorage, (window||globalThis||self).sessionStorage).
 */
var WebStorageProvider = /** @class */ (function () {
    /**
     * Creates a WebStorageProvider using the provider storageType
     * @param storageType The type of Storage provider, normal values are "localStorage" or "sessionStorage"
     */
    function WebStorageProvider(storageType, id, unloadHookContainer) {
        dynamicProto(WebStorageProvider, this, function (_this) {
            var _storage = null;
            var _storageKeyPrefix = DefaultStorageKey;
            var _maxStorageSizeInBytes = DefaultMaxStorageSizeInBytes;
            var _payloadHelper = null;
            var _storageKey = null;
            var _endpoint = null;
            var _maxStorageTime = null;
            var _eventDropPerTime = null;
            var _maxCriticalCnt = null;
            var _notificationManager = null;
            _this.id = id;
            _storage = _getAvailableStorage(storageType) || null;
            _this["_getDbgPlgTargets"] = function () {
                return [_storageKey, _maxStorageSizeInBytes, _maxStorageTime];
            };
            _this[_DYN_INITIALIZE /* @min:%2einitialize */] = function (providerContext, endpointUrl) {
                if (!_storage) {
                    return false;
                }
                var storageConfig = providerContext[_DYN_STORAGE_CONFIG /* @min:%2estorageConfig */];
                var itemCtx = providerContext[_DYN_ITEM_CTX /* @min:%2eitemCtx */];
                _payloadHelper = new PayloadHelper(itemCtx[_DYN_DIAG_LOG /* @min:%2ediagLog */]());
                _endpoint = getEndpointDomain(endpointUrl || providerContext[_DYN_ENDPOINT /* @min:%2eendpoint */]);
                var autoClean = !!storageConfig.autoClean;
                _notificationManager = providerContext[_DYN_NOTIFICATION_MGR /* @min:%2enotificationMgr */];
                var unloadHook = onConfigChange(storageConfig, function () {
                    _maxStorageSizeInBytes = storageConfig.maxStorageSizeInBytes || DefaultMaxStorageSizeInBytes; // value checks and defaults should be applied during core config
                    _maxStorageTime = storageConfig[_DYN_IN_STORAGE_MAX_TIME /* @min:%2einStorageMaxTime */] || DefaultMaxInStorageTime; // TODO: handle 0
                    var dropNum = storageConfig[_DYN__EVENTS_TO_DROP_PER_2 /* @min:%2eEventsToDropPerTime */];
                    _eventDropPerTime = isNotNullOrUndefined(dropNum) ? dropNum : EventsToDropAtOneTime;
                    _maxCriticalCnt = storageConfig[_DYN_MAX_CRITICAL_EVTS_DR3 /* @min:%2emaxCriticalEvtsDropCnt */] || MaxCriticalEvtsDropCnt;
                });
                unloadHookContainer && unloadHookContainer.add(unloadHook);
                // currently, won't handle endpoint change here
                // new endpoint will open a new db
                // endpoint change will be handled at offline batch level
                // namePrefix should not contain any "_"
                _storageKeyPrefix = storageConfig[_DYN_STORAGE_KEY_PREFIX /* @min:%2estorageKeyPrefix */] || DefaultStorageKey;
                _storageKey = _storageKeyPrefix + "_" + Version + "_" + _endpoint;
                if (autoClean) {
                    // won't wait response here
                    _this.clean();
                }
                // TODO: handle versoin Upgrade
                //_checkVersion();
                return true;
            };
            /**
              * Identifies whether this storage provider support synchronous requests
             */
            _this[_DYN_SUPPORTS_SYNC_REQUES4 /* @min:%2esupportsSyncRequests */] = function () {
                return true;
            };
            /**
             * Get all of the currently cached events from the storage mechanism
             */
            _this.getAllEvents = function (cnt) {
                try {
                    if (!_storage) {
                        // if not init, return null
                        return;
                    }
                    return _getEvts(cnt);
                }
                catch (e) {
                    return createAsyncRejectedPromise(e);
                }
            };
            /**
             * Get Next cached event from the storage mechanism
             */
            _this[_DYN_GET_NEXT_BATCH /* @min:%2egetNextBatch */] = function () {
                try {
                    if (!_storage) {
                        // if not init, return null
                        return;
                    }
                    // set ordered to true, to make sure to get earliest events first
                    return _getEvts(1, true);
                }
                catch (e) {
                    return createAsyncRejectedPromise(e);
                }
            };
            function _getEvts(cnt, ordered) {
                var allItems = [];
                var theStore = _fetchStoredDb(_storageKey).db;
                if (theStore) {
                    var events = theStore[_DYN_EVTS /* @min:%2eevts */];
                    forEachMap(events, function (evt) {
                        if (evt) {
                            if (evt[_DYN_IS_ARR /* @min:%2eisArr */]) {
                                evt = _payloadHelper.base64ToArr(evt);
                            }
                            allItems[_DYN_PUSH /* @min:%2epush */](evt);
                        }
                        if (cnt && allItems && allItems[_DYN_LENGTH /* @min:%2elength */] == cnt) {
                            return false;
                        }
                        return true;
                    }, ordered);
                }
                return allItems;
            }
            /**
             * Stores the value into the storage using the specified key.
             * @param key - The key value to use for the value
             * @param value - The actual value of the request
             */
            _this[_DYN_ADD_EVENT /* @min:%2eaddEvent */] = function (key, evt, itemCtx) {
                try {
                    var theStore = _fetchStoredDb(_storageKey);
                    evt.id = evt.id || getTimeId();
                    evt[_DYN_CRITICAL_CNT /* @min:%2ecriticalCnt */] = evt[_DYN_CRITICAL_CNT /* @min:%2ecriticalCnt */] || 0;
                    var events = theStore.db[_DYN_EVTS /* @min:%2eevts */];
                    var id_1 = evt.id;
                    if (evt && evt[_DYN_IS_ARR /* @min:%2eisArr */]) {
                        evt = _payloadHelper.base64ToStr(evt);
                    }
                    var preDroppedCnt = 0;
                    // eslint-disable-next-line no-constant-condition
                    while (true && evt) {
                        events[id_1] = evt;
                        if (_updateStoredDb(theStore)) {
                            // Database successfully updated
                            if (preDroppedCnt && _notificationManager) {
                                // only send notification when batches are updated successfully in storage
                                batchDropNotification(_notificationManager, preDroppedCnt, 2 /* eBatchDiscardedReason.CleanStorage */);
                            }
                            return evt;
                        }
                        // Could not not add events to storage assuming its full, so drop events to make space
                        // or max size exceeded
                        delete events[id_1];
                        var droppedCnt = _dropEventsUpToPersistence(_maxCriticalCnt, events, _eventDropPerTime);
                        preDroppedCnt += droppedCnt;
                        if (!droppedCnt) {
                            // Can't free any space for event
                            return createAsyncRejectedPromise(new Error("Unable to free up event space"));
                        }
                    }
                }
                catch (e) {
                    return createAsyncRejectedPromise(e);
                }
            };
            /**
             * Removes the value associated with the provided key
             * @param evts - The events to be removed
             */
            _this[_DYN_REMOVE_EVENTS /* @min:%2eremoveEvents */] = function (evts) {
                try {
                    var theStore = _fetchStoredDb(_storageKey, false);
                    var currentDb = theStore.db;
                    if (currentDb) {
                        var events = currentDb[_DYN_EVTS /* @min:%2eevts */];
                        try {
                            for (var i = 0; i < evts[_DYN_LENGTH /* @min:%2elength */]; ++i) {
                                var evt = evts[i];
                                delete events[evt.id];
                            }
                            // Update takes care of removing the DB if it's completely empty now
                            if (_updateStoredDb(theStore)) {
                                return evts;
                            }
                        }
                        catch (e) {
                            // Storage corrupted
                        }
                        // failure here so try and remove db to unblock following events
                        evts = _clearDatabase(theStore.key);
                    }
                    return evts;
                }
                catch (e) {
                    return createAsyncRejectedPromise(e);
                }
            };
            /**
             * Removes all entries from the storage provider for the current endpoint and returns them as part of the response, if there are any.
             */
            _this[_DYN_CLEAR /* @min:%2eclear */] = function () {
                try {
                    var removedItems_1 = [];
                    var theStore = _fetchStoredDb(_storageKey, false);
                    var storedDb = theStore.db;
                    if (storedDb) {
                        var events_1 = storedDb[_DYN_EVTS /* @min:%2eevts */];
                        forEachMap(events_1, function (evt) {
                            if (evt) {
                                delete events_1[evt.id];
                                removedItems_1[_DYN_PUSH /* @min:%2epush */](evt);
                            }
                            return true;
                        });
                        _updateStoredDb(theStore);
                    }
                    return removedItems_1;
                }
                catch (e) {
                    // Unable to clear the database
                    return createAsyncRejectedPromise(e);
                }
            };
            _this.clean = function () {
                var storeDetails = _fetchStoredDb(_storageKey, false);
                var currentDb = storeDetails.db;
                if (currentDb) {
                    var events = currentDb[_DYN_EVTS /* @min:%2eevts */];
                    try {
                        var isDropped = _dropMaxTimeEvents(_maxStorageTime, events, _eventDropPerTime, _notificationManager);
                        if (isDropped) {
                            return _updateStoredDb(storeDetails);
                        }
                        return true;
                    }
                    catch (e) {
                        // should not throw errors here
                        // because we don't want to block following process
                    }
                    return false;
                }
            };
            /**
             * Shuts-down the telemetry plugin. This is usually called when telemetry is shut down.
             * This attempts to update the lastAccessTime for any storedDb
             */
            _this[_DYN_TEARDOWN /* @min:%2eteardown */] = function () {
                try {
                    var theStore = _fetchStoredDb(_storageKey, false);
                    var storedDb = theStore.db;
                    if (storedDb) {
                        // reset the last access time
                        storedDb[_DYN_LAST_ACCESS_TIME /* @min:%2elastAccessTime */] = 0;
                        _updateStoredDb(theStore, false);
                    }
                }
                catch (e) {
                    // Add diagnostic logging
                }
            };
            /**
             * @ignore
             * Creates a new json store with the StorageJSON (may be null), a null db value indicates that the store
             * associated with the key is empty and should be removed.
             * @param dbKey
             * @param forceRemove
             */
            function _newStore(dbKey, db) {
                return {
                    key: dbKey,
                    db: db
                };
            }
            function _fetchStoredDb(dbKey, returnDefault) {
                var _a;
                if (returnDefault === void 0) { returnDefault = true; }
                var dbToStore = null;
                if (_storage) {
                    var previousDb = _storage.getItem(dbKey);
                    if (previousDb) {
                        try {
                            dbToStore = getJSON().parse(previousDb);
                        }
                        catch (e) {
                            // storage corrupted
                            _storage[_DYN_REMOVE_ITEM /* @min:%2eremoveItem */](dbKey);
                        }
                    }
                    if (returnDefault && !dbToStore) {
                        // Create and return a default empty database
                        dbToStore = (_a = {
                                evts: {}
                            },
                            _a[_DYN_LAST_ACCESS_TIME /* @min:lastAccessTime */] = 0,
                            _a);
                    }
                }
                return _newStore(dbKey, dbToStore);
            }
            function _updateStoredDb(jsonStore, updateLastAccessTime) {
                if (updateLastAccessTime === void 0) { updateLastAccessTime = true; }
                //let removeDb = true;
                var dbToStore = jsonStore.db;
                if (dbToStore) {
                    if (updateLastAccessTime) {
                        // Update the last access time
                        dbToStore.lastAccessTime = (new Date())[_DYN_GET_TIME /* @min:%2egetTime */]();
                    }
                }
                try {
                    var jsonString = getJSON().stringify(dbToStore);
                    if (jsonString[_DYN_LENGTH /* @min:%2elength */] > _maxStorageSizeInBytes) {
                        // We can't store the database as it would exceed the configured max size
                        return false;
                    }
                    _storage && _storage.setItem(jsonStore.key, jsonString);
                    //}
                }
                catch (e) {
                    // catch exception due to trying to store or clear JSON
                    // We could not store the database
                    return false;
                }
                return true;
            }
            function _clearDatabase(dbKey) {
                var removedItems = [];
                var storeDetails = _fetchStoredDb(dbKey, false);
                var currentDb = storeDetails.db;
                if (currentDb) {
                    var events = currentDb[_DYN_EVTS /* @min:%2eevts */];
                    try {
                        forEachMap(events, function (evt) {
                            if (evt) {
                                removedItems[_DYN_PUSH /* @min:%2epush */](evt);
                            }
                            return true;
                        });
                    }
                    catch (e) {
                        // catch exception due to trying to store or clear JSON
                    }
                    // Remove the entire stored database
                    _storage && _storage[_DYN_REMOVE_ITEM /* @min:%2eremoveItem */](storeDetails.key);
                }
                return removedItems;
            }
        });
    }
    /**
     * Initializes the provider using the config
     * @param providerContext The provider context that should be used to initialize the provider
     * @returns True if the provider is initialized and available for use otherwise false
     */
    WebStorageProvider.prototype.initialize = function (providerContext) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return false;
    };
    /**
     * Identifies whether this storage provider support synchronous requests
    */
    WebStorageProvider.prototype.supportsSyncRequests = function () {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return false;
    };
    /**
     * Get all of the currently cached events from the storage mechanism
     */
    WebStorageProvider.prototype.getAllEvents = function (cnt) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return null;
    };
    /**
     * Get the Next one cached batch from the storage mechanism
     */
    WebStorageProvider.prototype.getNextBatch = function () {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return null;
    };
    /**
     * Stores the value into the storage using the specified key.
     * @param key - The key value to use for the value
     * @param evt - The actual event of the request
     * @param itemCtx - This is the context for the current request, ITelemetryPlugin instances
     * can optionally use this to access the current core instance or define / pass additional information
     * to later plugins (vs appending items to the telemetry item)
     */
    WebStorageProvider.prototype.addEvent = function (key, evt, itemCtx) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return;
    };
    /**
     * Removes the value associated with the provided key
     * @param evts - The events to be removed
     */
    WebStorageProvider.prototype.removeEvents = function (evts) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return;
    };
    /**
     * Removes all entries from the storage provider, if there are any.
     */
    WebStorageProvider.prototype.clear = function () {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return;
    };
    /**
     * Removes all entries with stroage time longer than inStorageMaxTime from the storage provider
     */
    WebStorageProvider.prototype.clean = function () {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return;
    };
    /**
     * Shuts-down the telemetry plugin. This is usually called when telemetry is shut down.
     */
    WebStorageProvider.prototype.teardown = function () {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
    };
    return WebStorageProvider;
}());
export { WebStorageProvider };
//# sourceMappingURL=WebStorageProvider.js.map